import * as React from 'react';
import { Dialog as RD } from 'radix-ui';
import { X } from 'lucide-react';
import { cn } from '@client/lib/utils';
import { Button } from './button';

/** RxDrawer: right-side sheet on the panel shadow; the scrim is the brand ink at 45% with a light blur. */
export function Sheet({ open, onOpenChange, title, description, children, width = 'max-w-[560px]', footer, headerExtra, bodyClassName }: { open: boolean; onOpenChange: (o: boolean) => void; title: React.ReactNode; description?: React.ReactNode; children: React.ReactNode; width?: string; footer?: React.ReactNode; headerExtra?: React.ReactNode; bodyClassName?: string }) {
  return (
    <RD.Root open={open} onOpenChange={onOpenChange}>
      <RD.Portal>
        <RD.Overlay className="fixed inset-0 z-40 bg-[var(--scrim)] backdrop-blur-[2px]" />
        <RD.Content className={cn('fixed inset-y-0 right-0 z-50 flex w-full flex-col bg-popover shadow-panel animate-drawer md:my-2 md:mr-2 md:h-[calc(100dvh-16px)] md:rounded-[24px] max-md:max-w-full', width)}>
          <div className="flex items-start justify-between gap-4 border-b border-border px-6 py-4">
            <div className="min-w-0">
              <RD.Title className="section-title truncate">{title}</RD.Title>
              {description ? <RD.Description className="mt-0.5 text-[12.5px] text-muted">{description}</RD.Description> : <RD.Description className="sr-only">{typeof title === 'string' ? title : 'Panel'}</RD.Description>}
            </div>
            <div className="flex items-center gap-2">
              {headerExtra}
              <RD.Close asChild>
                <Button variant="ghost" size="icon-sm" aria-label="Close">
                  <X />
                </Button>
              </RD.Close>
            </div>
          </div>
          <div className={cn('rx-scroll flex-1 overflow-y-auto px-6 py-5', bodyClassName)}>{children}</div>
          {footer ? <div className="flex flex-wrap items-center justify-end gap-2 border-t border-border px-6 py-3">{footer}</div> : null}
        </RD.Content>
      </RD.Portal>
    </RD.Root>
  );
}

/** RxDialog: centred, 24px radius, dialog shadow. Never a native confirm(). */
export function Dialog({ open, onOpenChange, title, description, children, footer, width = 'max-w-md' }: { open: boolean; onOpenChange: (o: boolean) => void; title: string; description?: React.ReactNode; children?: React.ReactNode; footer?: React.ReactNode; width?: string }) {
  return (
    <RD.Root open={open} onOpenChange={onOpenChange}>
      <RD.Portal>
        <RD.Overlay className="fixed inset-0 z-40 bg-[var(--scrim)] backdrop-blur-[2px]" />
        <RD.Content className={cn('fixed left-1/2 top-1/2 z-50 w-[calc(100%-32px)] -translate-x-1/2 -translate-y-1/2 rounded-panel bg-popover p-6 shadow-dialog animate-pop dark:border dark:border-border', width)}>
          <RD.Title className="text-[17px] font-[700] leading-6 tracking-[-0.012em] text-foreground">{title}</RD.Title>
          {description ? <RD.Description className="mt-1 text-[13px] leading-5 text-muted">{description}</RD.Description> : <RD.Description className="sr-only">{title}</RD.Description>}
          {children ? <div className="mt-4">{children}</div> : null}
          {footer ? <div className="mt-6 flex justify-end gap-2">{footer}</div> : null}
        </RD.Content>
      </RD.Portal>
    </RD.Root>
  );
}

export function ConfirmDialog({ open, onOpenChange, title, description, confirmLabel, cancelLabel = 'Cancel', destructive, onConfirm, loading }: { open: boolean; onOpenChange: (o: boolean) => void; title: string; description?: React.ReactNode; confirmLabel: string; cancelLabel?: string; destructive?: boolean; onConfirm: () => void; loading?: boolean }) {
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      description={description}
      footer={
        <>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            {cancelLabel}
          </Button>
          <Button variant={destructive ? 'destructive' : 'primary'} onClick={onConfirm} loading={loading}>
            {confirmLabel}
          </Button>
        </>
      }
    />
  );
}
